'use client';

import { useState, useEffect } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

import { useTranslations } from '@northladder/i18n';
import { ICoordinate } from '@northladder/services';

import { IModalAction, SimpleModal } from '../../../../modals';

import { IBaseGoogleMapLocationOptions } from '../../types';

import { GoogleMap } from '../GoogleMap';
import { GoogleMapsProvider } from '../GoogleMapsProvider';

type TGoogleMapModalProps = Pick<
  IBaseGoogleMapLocationOptions,
  'initLocation' | 'onUpdateLocation' | 'regionKey'
> & {
  isModalOpen: boolean;
  onCloseModal: () => void;
};

/**
 * -----------------------------------------------------------------------------
 * Renders the base modal map model that allows for drag and drop and search.
 */
export function GoogleMapModal({
  initLocation,
  isModalOpen,
  onCloseModal,
  onUpdateLocation,
  regionKey,
}: TGoogleMapModalProps) {
  const t = useTranslations('DesignSystemComponents.gpsField');
  const [selectedLocation, setSelectedLocation] = useState<ICoordinate | null>(
    initLocation
  );

  // Update selectedLocation when initLocation or modal state changes
  useEffect(() => {
    if (isModalOpen) {
      setSelectedLocation(initLocation);
    }
  }, [initLocation, isModalOpen]);

  /**
   * Keep a copy of the selected location in the modal to allow for saving or
   * cancelling to the previous state.
   */
  const handleOnUpdateSelectedLocation = (newLocation: ICoordinate) => {
    setSelectedLocation(newLocation);
  };

  const handleCancel = () => {
    onUpdateLocation(initLocation);
    onCloseModal();
  };

  const handleSave = () => {
    onUpdateLocation(selectedLocation);
    onCloseModal();
  };

  const modalActions: IModalAction[] = [
    {
      id: 'cancel',
      label: t('cancelLocation'),
      onClick: handleCancel,
      type: 'secondary',
    },
    {
      id: 'confirm',
      label: t('confirmLocation'),
      onClick: handleSave,
      type: 'primary',
    },
  ];

  return (
    <GoogleMapsProvider
      mapZoom={initLocation?.zoom}
      regionKey={regionKey}
      renderMapContainer={(ref) => (
        <SimpleModal
          actions={modalActions}
          isOpen={isModalOpen}
          onCloseModal={onCloseModal}
        >
          <div className="flex flex-col">
            {/* Coordinate Display Header */}
            {selectedLocation ? (
              <div
                className="
                  bg-muted/50 border-border flex items-center
                  justify-between gap-4 border-b px-4 py-3 sm:px-6
                "
              >
                <div className="flex items-center gap-2">
                  <MapPinIcon
                    className="text-primary h-5 w-5 shrink-0"
                    strokeWidth={2}
                  />
                  <span className="text-foreground text-sm font-medium">
                    {t('selectedLocation')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs font-medium">
                      {t('latitude')}
                    </span>
                    <code
                      className="
                        bg-background text-foreground border-border rounded
                        border px-2 py-1 font-mono text-xs
                      "
                    >
                      {selectedLocation.lat.toPrecision(8)}
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs font-medium">
                      {t('longitude')}
                    </span>
                    <code
                      className="
                        bg-background text-foreground border-border rounded
                        border px-2 py-1 font-mono text-xs
                      "
                    >
                      {selectedLocation.lng.toPrecision(8)}
                    </code>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Map Container */}
            <div
              className="
                flex h-[calc(100vh-2rem)] w-[calc(100vw-1rem)]
                sm:h-[calc(100vh-5rem)] sm:w-full
              "
            >
              <GoogleMap
                ref={ref}
                initLocation={selectedLocation}
                onUpdateLocation={handleOnUpdateSelectedLocation}
                regionKey={regionKey}
              />
            </div>
          </div>
        </SimpleModal>
      )}
    />
  );
}
